import {
  Check,
  Palette,
  PenLine,
  Plus,
  Tag,
  Tags,
  Trash2,
  X,
} from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { type ModalProps, Pressable } from "react-native";
import { TouchableOpacity } from "react-native-gesture-handler";
import { Circle, View, XStack, YStack } from "tamagui";
import type PlanModel from "../../plans/PlanModel";
import type { PlanTag } from "../../plans/PlanModel";
import { Button } from "../../ui/Button";
import { Group } from "../../ui/Group";
import { Input } from "../../ui/InputNew";
import { Label, LabelTextarea } from "../../ui/Label";
import { Modal } from "../../ui/Modal";
import { PopoverConfirm } from "../../ui/PopoverConfirm";
import { Subtext } from "../../ui/Subtext";
import { TipsText } from "../../ui/TipsText";
import { Title } from "../../ui/Title";
import { useStores } from "../stores";
import { trackEvent } from "../trackEvent";
import { getTagColorNotUsed, TagColorsAvailable } from "./types";

const TagRow = observer(
  ({
    tag,
    plan,
    isSelected,
    onSelect,
  }: {
    tag: PlanTag;
    plan: PlanModel;
    onSelect?: (tag: PlanTag) => void;
    isSelected?: boolean;
  }) => {
    const [showColors, setShowColors] = React.useState(false);
    const [showEdit, setShowEdit] = React.useState(false);

    return (
      <YStack gap="$medium" width="100%">
        <XStack
          width={"100%"}
          justifyContent="space-between"
          alignItems="center"
          gap="$space.medium"
        >
          <XStack
            flex={1}
            cursor="pointer"
            justifyContent="space-between"
            alignItems="center"
            gap="$space.medium"
          >
            {isSelected && <Check size={"$size.normal"} />}

            <TouchableOpacity
              onPress={() => {
                setShowColors(!showColors);
              }}
            >
              <Circle style={{ backgroundColor: tag.color }} size={24} />
            </TouchableOpacity>

            <View flex={1}>
              {showEdit && (
                <LabelTextarea
                  placeholder="Set label..."
                  submitOnEnter
                  preventNewLine
                  blurOnSubmit
                  submitOnBlur
                  flex={1}
                  clearOnSubmit
                  type="border"
                  strikeThrough={tag.archived}
                  onSubmitEditing={(e) => {
                    if (e.nativeEvent.text) {
                      plan.updateTag({
                        ...tag,
                        label: e.nativeEvent.text,
                      });
                      trackEvent("tag_label_changed");
                      setShowEdit(false);
                    }
                  }}
                  defaultValue={tag.label}
                />
              )}
              {!showEdit && (
                <Pressable
                  style={{ width: "100%" }}
                  disabled={!onSelect}
                  onPress={() => onSelect?.(tag)}
                >
                  <Label>{tag.label}</Label>
                </Pressable>
              )}
            </View>
          </XStack>

          {!showEdit && (
            <TouchableOpacity onPress={() => setShowEdit(!showEdit)}>
              <PenLine />
            </TouchableOpacity>
          )}

          <PopoverConfirm
            onConfirm={() => {
              plan.updateTag({ ...tag, archived: true });
              trackEvent("tag_archived");
            }}
          >
            <Trash2 size={"$size.normal"} color={"$muted"} />
          </PopoverConfirm>
        </XStack>

        {showColors && (
          <XStack
            flexWrap="wrap"
            gap="$small"
            justifyContent="space-between"
            alignItems="center"
          >
            <Palette size={"$medium"} />
            {TagColorsAvailable.map((color) => {
              if (color.color === tag.color) {
                return null;
              }

              return (
                <TouchableOpacity
                  key={color.color}
                  onPress={() => {
                    plan.updateTag({ ...tag, ...color });
                    trackEvent("tag_color_changed");
                    setShowColors(false);
                  }}
                >
                  <Circle
                    key={color.color}
                    style={{ backgroundColor: color.color }}
                    size="$medium"
                  />
                </TouchableOpacity>
              );
            })}
          </XStack>
        )}
      </YStack>
    );
  },
);

export const TagsModal = observer(
  ({
    planUid,
    selectedTags,
    onSelect,
    visible,
    onClose,
    ...props
  }: {
    onSelect?: (tag: PlanTag) => void;
    selectedTags?: PlanTag[];
    planUid: string;
    onClose: () => void;
  } & ModalProps) => {
    const { plansStore } = useStores();

    const plan = plansStore.byId.get(planUid);

    if (!plan) {
      return null;
    }

    const tags = plan.availableTags;

    const isSelectMode = !!onSelect;

    return (
      <Modal
        modalId="tags"
        onClose={onClose}
        visible={visible}
        {...props}
        header={
          <YStack gap="$space.medium">
            <XStack gap="$medium" alignItems="center">
              <Tags />
              <Title>{isSelectMode ? "Select tags" : "Manage tags"}</Title>
            </XStack>
            <Subtext>Edit tags or delete them if not needed anymore</Subtext>
          </YStack>
        }
        footer={
          <Button
            type="secondary"
            icon={X}
            onPress={() => {
              onClose();
            }}
          >
            Close
          </Button>
        }
      >
        <YStack gap="$space.medium">
          {!isSelectMode && (
            <TipsText text="A tag can be used in several places throughout your Planira" />
          )}

          <Group borderTop={false} borderBottom={true}>
            {tags.map((tag) => (
              <TagRow
                key={tag.uid}
                onSelect={onSelect}
                isSelected={!!selectedTags?.find((t) => t.uid === tag.uid)}
                tag={tag}
                plan={plan}
              />
            ))}
          </Group>

          <XStack width={"100%"} alignItems="center" gap="$space.medium">
            <Tag />
            <Input
              autoCorrect
              preventNewLine
              defaultValue={""}
              type="border"
              placeholder={"Add new tag..."}
              submitOnEnter
              blurOnSubmit={false}
              attention={tags.length === 0}
              clearOnSubmit
              icon={Plus}
              onSubmitEditing={async (e) => {
                const label = e.nativeEvent.text;
                const color = getTagColorNotUsed(tags);

                const tagAdded = await plan.addTag(
                  label,
                  color.color,
                  color.textColor,
                );
                if (isSelectMode && tagAdded) {
                  onSelect(tagAdded);
                }
              }}
            />
          </XStack>
        </YStack>
      </Modal>
    );
  },
);
